import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api";
import {
  ACCEPT_ATTR,
  ACCEPTED_FORMATS_MESSAGE,
  isAcceptedFile,
  MAX_BYTES,
  MAX_FILES,
  formatBytes,
} from "@/lib/attachments";

type Props = {
  conversationId: string;
  initialCount?: number;
  disabled?: boolean;
};

type AttachmentListResponse =
  | unknown[]
  | { attachments?: unknown[]; items?: unknown[]; count?: number; total?: number }
  | null;

function countFromResponse(payload: AttachmentListResponse): number | null {
  if (Array.isArray(payload)) return payload.length;
  if (!payload || typeof payload !== "object") return null;
  const value = payload as Exclude<AttachmentListResponse, unknown[] | null>;
  if (Array.isArray(value.attachments)) return value.attachments.length;
  if (Array.isArray(value.items)) return value.items.length;
  if (typeof value.count === "number") return value.count;
  if (typeof value.total === "number") return value.total;
  return null;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError && error.message) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return "La pièce jointe n'a pas pu être versée au dossier.";
}

export function ConversationAttachmentButton({
  conversationId,
  initialCount = 0,
  disabled = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [count, setCount] = useState(Math.min(MAX_FILES, Math.max(0, initialCount)));
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void apiFetch<AttachmentListResponse>(
      `/api/chat/${encodeURIComponent(conversationId)}/attachments`,
    )
      .then((payload) => {
        const serverCount = countFromResponse(payload);
        if (!cancelled && serverCount !== null) {
          setCount(Math.min(MAX_FILES, Math.max(0, serverCount)));
        }
      })
      .catch(() => {
        // La liste est un confort d'affichage : le bouton reste utilisable si
        // le GET est indisponible, et le serveur demeure l'autorité du plafond.
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      setError(null);
      const current = count;
      const remaining = MAX_FILES - current;
      if (remaining <= 0) {
        setError(`Cette séance porte déjà ${current} pièces — c'est le maximum.`);
        return;
      }

      const selected = Array.from(files).slice(0, remaining);
      if (Array.from(files).length > remaining) {
        setError(`Cette séance porte déjà ${current} pièces — c'est le maximum.`);
      }
      const valid = selected.filter((file) => {
        if (!isAcceptedFile(file)) {
          setError(`${file.name} : format refusé. ${ACCEPTED_FORMATS_MESSAGE}`);
          return false;
        }
        if (file.size > MAX_BYTES) {
          setError(`${file.name} : fichier trop lourd (${formatBytes(file.size)}, 15 Mo maximum).`);
          return false;
        }
        return true;
      });
      if (valid.length === 0) return;

      setUploading(true);
      await Promise.all(
        valid.map(async (file) => {
          const form = new FormData();
          form.append("file", file);
          try {
            await apiFetch(`/api/chat/${encodeURIComponent(conversationId)}/attachments`, {
              method: "POST",
              body: form,
            });
            setCount((value) => Math.min(MAX_FILES, value + 1));
          } catch (uploadError) {
            setError(errorMessage(uploadError));
          }
        }),
      );
      setUploading(false);
    },
    [conversationId, count],
  );

  const isDisabled = disabled || uploading || count >= MAX_FILES;

  return (
    <div className="qorum-conversation-attachments">
      <style>{`
        .qorum-conversation-attachments { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .qorum-conversation-attachments-button {
          display: inline-flex; align-items: center; justify-content: center;
          width: 28px; height: 28px; padding: 0; border: 1px solid currentColor;
          background: transparent; color: inherit; cursor: pointer;
        }
        .qorum-conversation-attachments-button:hover:not(:disabled),
        .qorum-conversation-attachments-button:focus-visible:not(:disabled) { color: #FF1744; border-color: #FF1744; }
        .qorum-conversation-attachments-button:disabled { opacity: .35; cursor: not-allowed; }
        .qorum-conversation-attachments-count { font-family: var(--qv3-font-mono, ui-monospace, monospace); font-size: 10px; letter-spacing: .08em; }
        .qorum-conversation-attachments-hint, .qorum-conversation-attachments-limit { flex-basis: 100%; margin: 0; font-family: var(--qv3-font-sans, system-ui, sans-serif); font-size: 11px; line-height: 1.4; color: var(--qv3-grey-500, #8a8580); }
        .qorum-conversation-attachments-limit, .qorum-conversation-attachments-error { color: #FF1744; }
        .qorum-conversation-attachments-error { flex-basis: 100%; margin: 0; font-family: var(--qv3-font-sans, system-ui, sans-serif); font-size: 11px; line-height: 1.4; }
        .qorum-attachment-spin { animation: qorum-attachment-spin 1s linear infinite; }
        @keyframes qorum-attachment-spin { to { transform: rotate(360deg); } }
      `}</style>
      <button
        type="button"
        className="qorum-conversation-attachments-button"
        onClick={() => inputRef.current?.click()}
        disabled={isDisabled}
        aria-label={count >= MAX_FILES ? `Maximum de ${MAX_FILES} pièces atteint` : "Ajouter une pièce jointe"}
        title={count >= MAX_FILES ? `Cette séance porte déjà ${count} pièces — c'est le maximum.` : "Ajouter une pièce jointe"}
      >
        {uploading ? <Loader2 size={15} aria-hidden className="qorum-attachment-spin" /> : <Plus size={16} aria-hidden />}
      </button>
      <span className="qorum-conversation-attachments-count" aria-live="polite">{count}/{MAX_FILES}</span>
      <span className="qorum-conversation-attachments-count">PIÈCES</span>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        multiple
        hidden
        onChange={(event) => {
          if (event.target.files) void handleFiles(event.target.files);
          event.target.value = "";
        }}
      />
      {count >= MAX_FILES ? (
        <p className="qorum-conversation-attachments-limit" role="status">
          Cette séance porte déjà {count} pièces — c'est le maximum.
        </p>
      ) : (
        <p className="qorum-conversation-attachments-hint">{ACCEPTED_FORMATS_MESSAGE}</p>
      )}
      {error ? <p className="qorum-conversation-attachments-error" role="alert">{error}</p> : null}
    </div>
  );
}
